// components/InventoryManagement.jsx
import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';

// Import components
import Modal from '../ui/Modal';
import SearchBar from '../ui/SearchBar';
import InventoryCard from './InventoryCard';
import InventoryTable from './InventoryTable';
import CreateInventoryForm from './CreateInventoryForm';
import AddItemForm from './AddItemForm';

// Import utilities
import { 
  validateInventory, 
  validateItem, 
  createNewInventoryData, 
  createNewItemData 
} from '../../../../../utils/validation';

const InventoryManagement = () => {
  // State management
  const [inventoryData, setInventoryData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentInventoryIndex, setCurrentInventoryIndex] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Modal states
  const [modals, setModals] = useState({
    createInventory: false,
    addItem: false,
    viewInventory: false,
    editInventory: false
  });

  // Form states
  const [newInventory, setNewInventory] = useState({
    inventoryName: '', 
    description: '', 
    inventoryImage: null
  });

  const [newItem, setNewItem] = useState({
    productName: '', 
    productNo: '', 
    description: '', 
    weight: '', 
    quantity: ''
  });

  const [editInventory, setEditInventory] = useState({
    inventoryName: '', 
    description: '', 
    inventoryImage: null
  });

  // Modal management
  const openModal = (modalName, index = 0) => {
    setCurrentInventoryIndex(index);
    setModals(prev => ({ ...prev, [modalName]: true }));
    setFormErrors({});
  };

  const closeModal = (modalName) => {
    setModals(prev => ({ ...prev, [modalName]: false }));
    setFormErrors({});
    // Reset forms when closing
    if (modalName === 'createInventory') {
      setNewInventory({ inventoryName: '', description: '', inventoryImage: null });
    }
    if (modalName === 'addItem') {
      setNewItem({ productName: '', productNo: '', description: '', weight: '', quantity: '' });
    }
  };

  // Inventory operations
  const handleCreateInventory = () => {
    const errors = validateInventory(newInventory);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    
    setTimeout(() => {
      const newInventoryData = createNewInventoryData(newInventory);
      setInventoryData(prev => [...prev, newInventoryData]);
      setNewInventory({ inventoryName: '', description: '', inventoryImage: null });
      closeModal('createInventory');
      setIsLoading(false);
    }, 1000);
  };

  const handleAddItem = () => {
    const errors = validateItem(newItem);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const newItemData = createNewItemData(newItem);

    setInventoryData(prev => 
      prev.map((inventory, index) => 
        index === currentInventoryIndex
          ? { ...inventory, inventoryList: [...inventory.inventoryList, newItemData] }
          : inventory
      )
    );

    setNewItem({ productName: '', productNo: '', description: '', weight: '', quantity: '' });
    closeModal('addItem');
  };

  const handleEditInventory = () => {
    const errors = validateInventory(editInventory);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setInventoryData(prev =>
      prev.map((inventory, index) =>
        index === currentInventoryIndex
          ? {
              ...inventory,
              inventoryName: editInventory.inventoryName,
              description: editInventory.description,
              inventoryImage: editInventory.inventoryImage
            }
          : inventory
      )
    );

    closeModal('editInventory');
  };

  const handleDeleteInventory = (indexToDelete) => {
    if (window.confirm('Are you sure you want to delete this inventory?')) {
      setInventoryData(prev => prev.filter((_, index) => index !== indexToDelete));
    }
  };

  const handleDeleteItem = (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setInventoryData(prev =>
        prev.map((inventory, index) =>
          index === currentInventoryIndex
            ? {
                ...inventory,
                inventoryList: inventory.inventoryList.filter(item => item.id !== itemId)
              }
            : inventory
        )
      );
    }
  };

  // Image upload handlers
  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'create') {
          setNewInventory(prev => ({ ...prev, inventoryImage: reader.result }));
        } else if (type === 'edit') {
          setEditInventory(prev => ({ ...prev, inventoryImage: reader.result }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Filter inventories based on search
  const filteredInventories = inventoryData.filter(inventory =>
    inventory.inventoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inventory.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle edit inventory setup
  const handleEditSetup = (index) => {
    const inventory = inventoryData[index];
    setEditInventory({
      inventoryName: inventory.inventoryName,
      description: inventory.description,
      inventoryImage: inventory.inventoryImage
    });
    openModal('editInventory', index);
  };

  const handleShare = (index) => {
    const inventory = inventoryData[index];
    alert(`Sharing inventory: ${inventory.inventoryName}`);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Inventory</h1>
        
        {/* Search Bar */}
        {inventoryData.length > 0 && (
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            className="mb-6 max-w-md"
          />
        )}
        
        {/* Create New Inventory Button */}
        <div className="flex gap-4 mb-8">
          <div 
            className="bg-blue-100 rounded-lg p-6 cursor-pointer hover:bg-blue-200 transition-colors min-w-48"
            onClick={() => openModal('createInventory')}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-300 rounded-lg mx-auto mb-3 flex items-center justify-center">
                <Plus size={24} className="text-blue-700" />
              </div>
              <div className="text-blue-700 font-semibold">Create<br />Inventory</div>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Cards Grid */}
      {filteredInventories.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {filteredInventories.map((inventory, index) => (
            <InventoryCard
              key={inventory.id}
              inventory={inventory}
              index={inventoryData.indexOf(inventory)}
              onView={(i) => openModal('viewInventory', i)}
              onEdit={handleEditSetup}
              onAddItem={(i) => openModal('addItem', i)}
              onShare={handleShare}
              onDelete={handleDeleteInventory}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {inventoryData.length === 0 && (
        <EmptyState onCreateInventory={() => openModal('createInventory')} />
      )}

      {/* No Search Results */}
      {inventoryData.length > 0 && filteredInventories.length === 0 && (
        <NoSearchResults />
      )}

      {/* Modals */}
      <Modal 
        isOpen={modals.createInventory} 
        onClose={() => closeModal('createInventory')} 
        title="Create Inventory"
      >
        <CreateInventoryForm
          inventory={newInventory}
          onChange={setNewInventory}
          errors={formErrors}
          onImageChange={(e) => handleImageUpload(e, 'create')}
          onSubmit={handleCreateInventory}
          onCancel={() => closeModal('createInventory')}
          isLoading={isLoading}
        />
      </Modal>

      <Modal 
        isOpen={modals.addItem} 
        onClose={() => closeModal('addItem')} 
        title="Add Item to Inventory"
        size="lg"
      >
        <AddItemForm
          item={newItem}
          onChange={setNewItem}
          errors={formErrors}
          onSubmit={handleAddItem}
          onCancel={() => closeModal('addItem')}
        />
      </Modal>

      <Modal 
        isOpen={modals.viewInventory} 
        onClose={() => closeModal('viewInventory')} 
        title={`View Inventory: ${inventoryData[currentInventoryIndex]?.inventoryName || ''}`}
        size="xl"
      >
        {inventoryData[currentInventoryIndex] && (
          <ViewInventoryContent 
            inventory={inventoryData[currentInventoryIndex]}
            onDeleteItem={handleDeleteItem}
            onAddItem={() => {
              closeModal('viewInventory');
              openModal('addItem', currentInventoryIndex);
            }}
          />
        )}
      </Modal>

      <Modal 
        isOpen={modals.editInventory} 
        onClose={() => closeModal('editInventory')} 
        title="Edit Inventory"
      >
        <CreateInventoryForm
          inventory={editInventory}
          onChange={setEditInventory}
          errors={formErrors}
          onImageChange={(e) => handleImageUpload(e, 'edit')}
          onSubmit={handleEditInventory}
          onCancel={() => closeModal('editInventory')}
        />
      </Modal>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ onCreateInventory }) => (
  <div className="text-center py-12">
    <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
      <Plus size={32} className="text-gray-400" />
    </div>
    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Inventory Found</h3>
    <p className="text-gray-500 mb-6">Create your first inventory to get started</p>
    <button
      onClick={onCreateInventory}
      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
    >
      Create Inventory
    </button>
  </div>
);

// No Search Results Component
const NoSearchResults = () => (
  <div className="text-center py-12">
    <Search size={48} className="text-gray-400 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-gray-600 mb-2">No inventories found</h3>
    <p className="text-gray-500">Try searching with different keywords</p>
  </div>
);

// View Inventory Content Component
const ViewInventoryContent = ({ inventory, onDeleteItem, onAddItem }) => (
  <div className="p-6">
    <div className="bg-gray-50 rounded-lg p-4 mb-6">
      <div className="flex items-center gap-4">
        {inventory.inventoryImage && (
          <img
            src={inventory.inventoryImage}
            alt={inventory.inventoryName}
            className="w-16 h-16 object-cover rounded-lg"
          />
        )}
        <div>
          <h3 className="font-semibold text-lg">{inventory.inventoryName}</h3>
          <p className="text-gray-600">{inventory.description}</p>
          <p className="text-sm text-gray-500">
            {inventory.inventoryList.length} items in inventory
          </p>
        </div>
      </div>
    </div>
    
    <InventoryTable
      items={inventory.inventoryList}
      onDeleteItem={onDeleteItem}
      onAddItem={onAddItem}
    />
  </div>
);

export default InventoryManagement;